import { createFileRoute, Link } from "@tanstack/react-router";
import { useState } from "react";
import { useServerFn } from "@tanstack/react-start";
import { subscribeEmail } from "@/lib/newsletter.functions";
import { absoluteUrl } from "@/lib/site";
import { validateEmail } from "@/lib/email-validation";

export const Route = createFileRoute("/newsletter")({
  head: () => ({
    meta: [
      { title: "Newsletter · Buddha KA · KAfrequency" },
      { name: "description", content: "Receive the transmission — music drops, activations, wisdom, and behind-the-scenes notes from Buddha KA." },
      { property: "og:title", content: "Newsletter · Buddha KA" },
      { property: "og:description", content: "Receive the transmission — music, activations, and wisdom from Buddha KA." },
      { property: "og:type", content: "website" },
      { property: "og:url", content: absoluteUrl("/newsletter") },
      { property: "og:site_name", content: "Buddha KA · KAfrequency" },
      { name: "twitter:card", content: "summary_large_image" },
      { name: "twitter:title", content: "Newsletter · Buddha KA" },
      { name: "twitter:description", content: "Receive the transmission." },
    ],
    links: [{ rel: "canonical", href: absoluteUrl("/newsletter") }],
  }),
  component: NewsletterPage,
});

function NewsletterPage() {
  const [email, setEmail] = useState("");
  const [state, setState] = useState<"idle" | "sending" | "sent" | "error">("idle");
  const [message, setMessage] = useState<string | null>(null);
  const subscribe = useServerFn(subscribeEmail);

  async function submit(e: React.FormEvent) {
    e.preventDefault();
    const check = validateEmail(email);
    if (!check.ok) {
      setState("error");
      setMessage(check.error ?? "Enter a valid email address.");
      return;
    }
    setState("sending"); setMessage(null);
    try {
      await subscribe({ data: { email: check.normalized, source: "newsletter-page" } });
      setState("sent");
      setMessage("Check your inbox to confirm the transmission.");
      setEmail("");
    } catch (err) {
      setState("error");
      setMessage(err instanceof Error ? err.message : "Something went wrong. Try again.");
    }
  }

  return (
    <div className="mx-auto max-w-3xl px-4 py-20 sm:px-6 sm:py-28">
      <p className="text-xs uppercase tracking-[0.3em] text-gold">The Transmission</p>
      <h1 className="mt-4 font-serif text-5xl sm:text-6xl">Receive the frequency.</h1>
      <p className="mt-6 text-lg text-muted-foreground">
        A weekly note from Buddha KA: new music, activations, wisdom essays, and the occasional
        behind-the-scenes glimpse of the temple in construction. No noise. No filler. Unsubscribe anytime.
      </p>

      <form onSubmit={submit} className="mt-10 flex flex-col gap-3 sm:flex-row">
        <label htmlFor="newsletter-email" className="sr-only">Email</label>
        <input
          id="newsletter-email"
          type="email"
          required
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          placeholder="you@sacred.address"
          className="min-w-0 flex-1 rounded-full border hairline bg-obsidian px-5 py-3 text-sm placeholder:text-muted-foreground focus:border-gold focus:outline-none"
        />
        <button
          type="submit"
          disabled={state === "sending"}
          className="shrink-0 rounded-full bg-gold-gradient px-6 py-3 text-sm font-semibold uppercase tracking-[0.2em] text-obsidian disabled:opacity-60"
        >
          {state === "sending" ? "Sending…" : "Subscribe"}
        </button>
      </form>

      {message && (
        <p
          role="status"
          className={`mt-4 text-sm ${state === "error" ? "text-red-400" : "text-gold-luminous"}`}
        >
          {message}
        </p>
      )}

      <div className="mt-16 grid gap-6 border-t hairline pt-10 sm:grid-cols-2">
        <div>
          <p className="text-xs uppercase tracking-[0.25em] text-gold">What arrives</p>
          <ul className="mt-3 space-y-2 text-sm text-muted-foreground">
            <li>· New music and meditation drops before public release</li>
            <li>· Daily activation highlights and practice cycles</li>
            <li>· Essays, mirror thoughts, and teaching threads</li>
            <li>· Early access to books, courses, and offerings</li>
          </ul>
        </div>
        <div>
          <p className="text-xs uppercase tracking-[0.25em] text-gold">Explore</p>
          <ul className="mt-3 space-y-2 text-sm">
            <li><Link to="/activations" className="text-foreground/80 hover:text-gold">Today's activation →</Link></li>
            <li><Link to="/library" className="text-foreground/80 hover:text-gold">The Library →</Link></li>
            <li><Link to="/membership" className="text-foreground/80 hover:text-gold">Membership →</Link></li>
            <li><Link to="/wisdom" className="text-foreground/80 hover:text-gold">Wisdom archive →</Link></li>
          </ul>
        </div>
      </div>
    </div>
  );
}
